import type { AssetItem, AssetState } from "./assetmodel";

export type AssetContextType = "Workspace" | "Chat" | "Global" | "Other";

export const WORKSPACE_ASSET_EVENTS = {
  ViewRequested: "kci:workspace-asset-view-requested",
  ViewCompleted: "kci:workspace-asset-view-completed",
  FileView: "kci:workspace-file-view",
  PanelClosed: "kci:workspace-asset-panel-closed",
} as const;

export type WorkspaceAssetEventName = "Asset.ViewRequested" | "Asset.ViewCompleted";

export interface AssetEventEnvelope<TPayload> {
  eventId: string;
  correlationId: string;
  eventName: WorkspaceAssetEventName;
  tabId: string;
  contextType: AssetContextType;
  timestamp: number;
  payload: TPayload;
}

export interface AssetViewRequestedPayload {
  source: "nav-header" | "workspace-page";
}

export interface AssetViewCompletedPayload {
  success: boolean;
  assetState?: AssetState;
  files?: AssetItem[];
  error?: string;
}

export interface AssetFileViewPayload {
  file: AssetItem;
  tabId: string;
  label: string;
}

export interface AssetRequestContext {
  tabId: string;
  contextType: AssetContextType;
}

function readFirstNonEmptyString(keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = localStorage.getItem(key);
    if (value && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

function randomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function resolveAssetRequestContext(options: {
  activeTabId: string;
}): AssetRequestContext {
  const tabId =
    readFirstNonEmptyString([
      "kci.workspace.activeTabId",
      "kci.activeTabId",
      "activeTabId",
    ]) ?? options.activeTabId;


  const contextTypeRaw =
    readFirstNonEmptyString([
      "kci.workspace.contextType",
      "kci.contextType",
      "contextType",
      "ContextType",
    ]) ?? "Workspace";

  const normalizedContextType =
    contextTypeRaw === "Chat" || contextTypeRaw === "Global" ? contextTypeRaw : "Workspace";

  return {
    tabId,
    contextType: normalizedContextType,
  };
}

export function createAssetViewRequestedEnvelope(
  context: AssetRequestContext,
  payload: AssetViewRequestedPayload,
): AssetEventEnvelope<AssetViewRequestedPayload> {
  return {
    eventId: randomId(),
    correlationId: randomId(),
    eventName: "Asset.ViewRequested",
    tabId: context.tabId,
    contextType: context.contextType,
    timestamp: Date.now(),
    payload,
  };
}
